import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { DevolverPaqueteRequestSchema } from '@condo/contracts';
import type { DevolverPaqueteRequest } from '@condo/contracts';
import { useDevolverPaquete } from '@/lib/hooks/use-paquetes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface DevolverDialogProps {
  paqueteId: string;
  destinatarioNombre: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DevolverDialog({
  paqueteId,
  destinatarioNombre,
  open,
  onOpenChange,
}: DevolverDialogProps) {
  const mutation = useDevolverPaquete();

  const form = useForm<DevolverPaqueteRequest>({
    resolver: zodResolver(DevolverPaqueteRequestSchema),
    defaultValues: { razon: '' },
  });

  function handleClose(open: boolean) {
    if (!open) form.reset();
    onOpenChange(open);
  }

  async function onSubmit(values: DevolverPaqueteRequest) {
    try {
      await mutation.mutateAsync({ id: paqueteId, body: values });
      toast.success('Paquete devuelto', {
        description: `El paquete de ${destinatarioNombre} fue marcado como devuelto.`,
      });
      handleClose(false);
    } catch {
      toast.error('No se pudo registrar la devolucion');
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Devolver paquete</DialogTitle>
          <DialogDescription>
            Marca este paquete de {destinatarioNombre} como devuelto al remitente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="razon-devolver">Razon de devolucion</Label>
            <textarea
              id="razon-devolver"
              rows={3}
              className="mt-1.5 flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]"
              placeholder="Ej: No reclamado despues de 30 dias"
              {...form.register('razon')}
            />
            {form.formState.errors.razon && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.razon.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="outline" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Procesando…
                </>
              ) : (
                'Confirmar devolucion'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
